import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Search, UserPlus, MessageCircle, Check, Clock } from "lucide-react";

export default function Community() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [conns, setConns] = useState({ accepted: [], incoming: [], outgoing: [] });
  const [actingOn, setActingOn] = useState(null);

  const loadMembers = useCallback(() => {
    setLoading(true);
    const q = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/community/members${q}`)
      .then((r) => setMembers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search]);

  const loadConns = useCallback(() => {
    if (!user) return;
    api.get("/connections/mine").then((r) => setConns(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { loadConns(); }, [loadConns]);

  const connectionStatus = (ownerId) => {
    if (!user || !ownerId || ownerId === user.user_id) return "self";
    if (conns.accepted.some((c) => c.other_user?.user_id === ownerId)) return "accepted";
    if (conns.outgoing.some((c) => c.other_user?.user_id === ownerId)) return "outgoing";
    if (conns.incoming.some((c) => c.other_user?.user_id === ownerId)) return "incoming";
    return "none";
  };

  const requestConnect = async (ownerId) => {
    if (!user) { nav("/login"); return; }
    setActingOn(ownerId);
    try {
      await api.post("/connections/request", { to_user_id: ownerId });
      toast.success("Connection request sent.");
      loadConns();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setActingOn(null);
    }
  };

  const acceptIncoming = async (connectionId) => {
    setActingOn(connectionId);
    try {
      await api.post(`/connections/${connectionId}/accept`);
      toast.success("Connection accepted.");
      loadConns();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div data-testid="community-page">
      {/* HERO */}
      <section className="relative py-16 border-b border-white/5 splatter-blue grain">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="label text-pfc-gold mb-3">PFC · Collaboration Community</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
            The <span className="gold-text">Network.</span>
          </h1>
          <p className="text-white/65 mt-5 font-body text-lg max-w-2xl">
            Artists, models, athletes, founders, and filmmakers connecting, collaborating, and booking — all in one place.
          </p>
        </div>
      </section>

      {/* INCOMING REQUESTS */}
      {user && conns.incoming.length > 0 && (
        <section className="bg-pfc-gold/10 border-b border-pfc-gold/30" data-testid="community-incoming-requests">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5">
            <div className="label text-pfc-gold mb-3">Pending Connection Requests · {conns.incoming.length}</div>
            <div className="flex flex-wrap gap-3">
              {conns.incoming.map((c) => (
                <div key={c.connection_id} className="bg-ink-800 border border-white/15 px-4 py-3 flex items-center gap-3" data-testid={`incoming-${c.connection_id}`}>
                  <div>
                    <p className="font-display text-white text-base">{c.other_user?.name || "Member"}</p>
                    <p className="font-body text-white/50 text-xs">{c.other_user?.headline}</p>
                  </div>
                  <button
                    onClick={() => acceptIncoming(c.connection_id)}
                    disabled={actingOn === c.connection_id}
                    className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
                    data-testid={`accept-${c.connection_id}`}
                  >
                    Accept
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SEARCH + GRID */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        <div className="relative max-w-xl mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, headline, or tag…"
            className="w-full bg-ink-700 border border-white/10 text-white pl-11 pr-4 py-3 font-body focus:border-pfc-gold focus:outline-none"
            data-testid="community-search"
          />
        </div>

        {loading ? (
          <div className="text-white/40 text-center py-16 font-sub uppercase tracking-[0.2em]">Loading the network…</div>
        ) : members.length === 0 ? (
          <div className="text-white/40 text-center py-16 font-sub uppercase tracking-[0.2em]">No members found · adjust your search</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="community-grid">
            {members.map((m) => {
              const status = connectionStatus(m.owner_id);
              const initial = (m.name || "?").slice(0, 1).toUpperCase();
              return (
                <div key={m.profile_id} className="pfc-card p-5 flex flex-col" data-testid={`member-${m.profile_id}`}>
                  <div className="flex items-start gap-4 mb-4">
                    {m.avatar ? (
                      <img src={m.avatar} alt={m.name} className="w-14 h-14 object-cover" style={{ borderRadius: 6 }} />
                    ) : (
                      <div className="w-14 h-14 bg-pfc-gold/20 border border-pfc-gold/40 flex items-center justify-center font-display text-2xl text-pfc-gold" style={{ borderRadius: 6 }}>
                        {initial}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <Link to={`/profile/${m.profile_id}`} className="font-display text-xl text-white uppercase hover:text-pfc-gold transition truncate block">{m.name}</Link>
                      <p className="font-sub text-[10px] uppercase tracking-[0.2em] text-pfc-gold mt-1">{m.headline || m.category}</p>
                      {m.location && <p className="font-body text-white/50 text-xs mt-1">{m.location}</p>}
                    </div>
                    {m.featured && <span className="label bg-pfc-gold text-ink-900 px-2 py-1">Featured</span>}
                  </div>

                  <div className="mt-auto flex gap-2">
                    {status === "self" && (
                      <span className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 border border-white/15 text-white/40">You</span>
                    )}
                    {status === "none" && (
                      <button
                        onClick={() => requestConnect(m.owner_id)}
                        disabled={actingOn === m.owner_id || !m.owner_id}
                        className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 border border-pfc-gold/40 text-pfc-gold hover:bg-pfc-gold hover:text-ink-900 transition disabled:opacity-50 flex items-center gap-1.5 flex-1 justify-center"
                        data-testid={`connect-${m.profile_id}`}
                      >
                        <UserPlus size={12} /> Connect
                      </button>
                    )}
                    {status === "outgoing" && (
                      <span className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 border border-white/15 text-white/50 flex items-center gap-1.5 flex-1 justify-center">
                        <Clock size={12} /> Pending
                      </span>
                    )}
                    {status === "incoming" && (
                      <span className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 border border-pfc-gold/40 text-pfc-gold flex-1 text-center">
                        Wants to Connect
                      </span>
                    )}
                    {status === "accepted" && (
                      <span className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 bg-pfc-gold/15 border border-pfc-gold/40 text-pfc-gold flex items-center gap-1.5">
                        <Check size={12} /> Connected
                      </span>
                    )}
                    {user && m.owner_id && status !== "self" && (
                      <Link
                        to={`/messages/${m.owner_id}`}
                        className="font-sub uppercase tracking-[0.2em] text-[10px] px-3 py-2 bg-white/5 border border-white/15 text-white hover:border-pfc-gold hover:text-pfc-gold transition flex items-center gap-1.5"
                        data-testid={`message-${m.profile_id}`}
                      >
                        <MessageCircle size={12} /> Message
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
